import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fmtCoins, fmtUsd, coinsToUsd } from "../lib/api";
import { Coins, Zap, Flame, LogOut, LayoutDashboard, Wallet, Shield, Radar, Gamepad2 } from "lucide-react";
import { Button } from "./ui/button";
import AiChat from "./AiChat";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "dashboard-nav-link" },
  { to: "/play-earn", label: "Play & Earn", icon: Gamepad2, testid: "play-earn-nav-link" },
  { to: "/cashout", label: "Cashout", icon: Wallet, testid: "cashout-nav-link" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#07090E]/90 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-4">
          <NavLink to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
            <span className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/40">
              <Radar className="w-5 h-5 text-cyan-neon" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-400 radar-dot" />
            </span>
            <span className="font-display font-black text-lg tracking-tight hidden sm:block">
              ADS<span className="text-cyan-neon">RUSH</span>
            </span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isActive ? "bg-cyan-400/10 text-cyan-neon" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="admin-panel-nav-link"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isActive ? "bg-magenta-500/10 text-[#FF007A]" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Shield className="w-4 h-4" />
                Admin
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0C101A] border border-amber-500/30" data-testid="user-coin-balance">
              <Coins className="w-4 h-4 text-amber-neon" />
              <span className="font-mono-num font-semibold text-sm">{fmtCoins(user?.coins)}</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0C101A] border border-emerald-500/30" data-testid="user-dollar-balance">
              <Zap className="w-4 h-4 text-lime-neon" />
              <span className="font-mono-num font-semibold text-sm text-lime-neon">{fmtUsd(coinsToUsd(user?.coins))}</span>
            </div>
            {user?.streak > 0 && (
              <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0C101A] border border-orange-500/30" data-testid="streak-badge">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="font-mono-num text-sm">Day {user.streak}</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={doLogout}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`m-${l.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition ${
                  isActive ? "bg-cyan-400/10 text-cyan-neon" : "text-slate-400"
                }`
              }
            >
              <l.icon className="w-3.5 h-3.5" />
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#FF007A]">
              <Shield className="w-3.5 h-3.5" />Admin
            </NavLink>
          )}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6">{children}</main>
      <AiChat />
    </div>
  );
}
